import { useState, useEffect, useRef, ReactNode } from "react";

export const ScrollSection = ({
    children,
    showBreadcrumbs = true,
}: {
    children: ReactNode[] | ReactNode;
    showBreadcrumbs?: boolean;
}) => {
    const [activeSection, setActiveSection] = useState(0);
    const scrollContainerRef = useRef<HTMLDivElement>(null);

    // Ensure children is always an array
    const childrenArray = Array.isArray(children) ? children : [children];

    useEffect(() => {
        const scrollContainer = scrollContainerRef.current;
        if (!scrollContainer) return;

        const handleScroll = () => {
            const scrollPosition = scrollContainer.scrollTop;
            const viewportHeight = window.innerHeight;
            const sectionIndex = Math.round(scrollPosition / viewportHeight);
            setActiveSection(sectionIndex);
        };

        scrollContainer.addEventListener('scroll', handleScroll);
        // Initial check in case of refresh with scroll position
        handleScroll();
        
        return () => scrollContainer.removeEventListener('scroll', handleScroll);
    }, []);

    return (
        <div className="h-full overflow-hidden snap-y snap-mandatory scrollbar-hide relative">
            {/* Breadcrumb Navigation */}
            {showBreadcrumbs && (
                <nav className="fixed right-4 md:right-8 top-1/2 -translate-y-1/2 z-50 flex flex-col gap-4">
                    {childrenArray.map((_, index) => (
                        <a
                            key={index}
                            href={`#section-${index}`}
                            className={`w-4 md:w-8 h-3 rounded transition-all ${activeSection === index
                                ? 'bg-portfolio-green h-32'
                                : 'bg-slate-400 dark:bg-white/30 hover:bg-slate-600 dark:hover:bg-white/60 w-4'
                                }`}
                            aria-label={`Section ${index + 1}`}
                        />
                    ))}
                </nav>
            )}

            <div 
                ref={scrollContainerRef}
                className="h-full overflow-y-scroll snap-y snap-mandatory scrollbar-hide scroll-container"
            >
                {childrenArray.map((child, index) => (
                    <article
                        key={index}
                        id={`section-${index}`}
                        className="h-full w-full snap-start snap-always flex items-center justify-center overflow-hidden"
                    >
                        {child}
                    </article>
                ))}
            </div>
        </div>
    );
};
